import { useState, useEffect, useCallback, useRef } from 'react';

interface StripePrice {
  id: string;
  unit_amount: number;
  currency: string;
  recurring?: {
    interval: string;
    interval_count: number;
  };
  lookup_key?: string;
}

interface StripePrices {
  [key: string]: StripePrice;
}

interface PricesCache {
  data: StripePrices | null;
  timestamp: number;
  promise: Promise<StripePrices> | null;
}

// Cache global pour les prix (partagé entre les instances)
let globalPricesCache: PricesCache = {
  data: null,
  timestamp: 0,
  promise: null
};

const PRICES_CACHE_DURATION = 300000; // 5 minutes
const REFRESH_INTERVAL = 600000; // 10 minutes pour le refresh en arrière-plan

interface UseStripePricesOptions {
  apiUrl?: string;
  cacheDuration?: number;
  refreshInterval?: number;
}

export const useStripePrices = (options: UseStripePricesOptions = {}) => {
  const {
    apiUrl = 'http://localhost:3002/stripe-prices',
    cacheDuration = PRICES_CACHE_DURATION,
    refreshInterval = REFRESH_INTERVAL
  } = options;

  const [prices, setPrices] = useState<StripePrices | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);

  // Fonction pour récupérer les prix depuis l'API
  const fetchPrices = useCallback(async (forceRefresh = false): Promise<StripePrices> => {
    const now = Date.now();

    // Retourner le cache global si encore valide et pas de refresh forcé
    if (!forceRefresh && globalPricesCache.data && now - globalPricesCache.timestamp < cacheDuration) {
      return globalPricesCache.data;
    }

    // Retourner la promesse existante si une est en cours
    if (globalPricesCache.promise && !forceRefresh) {
      return globalPricesCache.promise;
    }

    // Créer une nouvelle promesse
    globalPricesCache.promise = (async () => {
      try {
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch prices: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        // Transformer les données pour correspondre au format attendu
        const transformedPrices: StripePrices = {};

        if (data.prices && typeof data.prices === 'object') {
          // Le backend retourne { prices: { 'price_id': {...} } }
          Object.keys(data.prices).forEach((key) => {
            const price = data.prices[key];
            transformedPrices[key] = {
              id: price.id,
              unit_amount: price.unit_amount,
              currency: price.currency,
              recurring: price.recurring,
              lookup_key: key
            };
          });
        }

        // Mettre à jour le cache global
        globalPricesCache.data = transformedPrices;
        globalPricesCache.timestamp = now;

        return transformedPrices;
      } catch (error) {
        console.error('Error fetching Stripe prices:', error);
        throw error;
      } finally {
        globalPricesCache.promise = null;
      }
    })();

    return globalPricesCache.promise;
  }, [apiUrl, cacheDuration]);

  // Charger les prix au montage
  useEffect(() => {
    const loadPrices = async () => {
      try {
        setLoading(true);
        setError(null);

        const pricesData = await fetchPrices();
        setPrices(pricesData);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load prices';
        setError(errorMessage);
        console.error('Error loading prices:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPrices();
  }, [fetchPrices]);

  // Configurer le refresh en arrière-plan
  useEffect(() => {
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    refreshIntervalRef.current = setInterval(async () => {
      try {
        setIsRefreshing(true);
        const pricesData = await fetchPrices();
        setPrices(pricesData);
        setError(null);
      } catch (error) {
        console.error('Background refresh error:', error);
        // Ne pas définir l'état d'erreur pour les échecs de refresh en arrière-plan
      } finally {
        setIsRefreshing(false);
      }
    }, refreshInterval);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchPrices, refreshInterval]);

  // Fonction de refresh manuel
  const refresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setError(null);

      const pricesData = await fetchPrices(true); // Refresh forcé
      setPrices(pricesData);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh prices';
      setError(errorMessage);
      console.error('Error refreshing prices:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchPrices]);

  return {
    prices,
    loading,
    isRefreshing,
    error,
    refresh,
  };
};
